import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, RefreshCcw, Quote } from "lucide-react";
import { API } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { StreetStickers, StickerMarquee } from "../components/StreetStickers";

const HERO = "https://images.pexels.com/photos/29538558/pexels-photo-29538558.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CATS = [
  { key: "streetwear", label: "Streetwear", img: "https://images.pexels.com/photos/36785698/pexels-photo-36785698.jpeg?auto=compress&cs=tinysrgb&w=900", desc: "Hoodies, oversized tees, cargos & heat" },
  { key: "casual", label: "Casual Wear", img: "https://images.unsplash.com/photo-1629753453388-5f3ae10370ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", desc: "Everyday shirts, denim & co-ords" },
];
const TESTIMONIALS = [
  { name: "Aarav M.", city: "Mumbai", text: "The Onyx blazer fits like it was tailored for me. Quality is genuinely luxury-tier." },
  { name: "Priya S.", city: "Bengaluru", text: "Ordered the Ghost Mask hoodie — the drop shoulder cut is perfect. Got compliments the same day." },
  { name: "Rohan K.", city: "Delhi", text: "Fast shipping, gold-standard packaging, and the sneakers are limited-drop worthy. Obsessed." },
];
const LOOKBOOK = [
  { img: "https://images.pexels.com/photos/29538558/pexels-photo-29538558.jpeg?auto=compress&cs=tinysrgb&w=800", title: "Editorial 01 — Gold Hour" },
  { img: "https://images.unsplash.com/photo-1768845431893-8e28ed7bf79e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", title: "Editorial 02 — Noir" },
  { img: "https://images.unsplash.com/photo-1516257984-b1b4d707412e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", title: "Drop — Denim Days" },
  { img: "https://images.unsplash.com/photo-1598539962077-e4185f37104f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", title: "Drop — Neon Nights" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const storyRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: storyRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  useEffect(() => {
    document.title = "Dressedin by Urbanz — Wear your identity.";
    API.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.products)).catch(() => {});
    API.get("/products", { params: { new_arrival: true, limit: 8 } }).then((r) => setNewArrivals(r.data.products)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[85vh] min-h-[560px] overflow-hidden">
        <motion.div initial={{ scale: 1.12, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: "easeOut" }} className="absolute inset-0">
          <video poster={HERO} autoPlay muted loop playsInline preload="auto"
            onLoadedData={(e) => e.target.play().catch(() => {})}
            data-testid="hero-video" className="h-full w-full object-cover">
            <source src="/hero.webm" type="video/webm" />
            <source src="/hero.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="absolute inset-0 bg-black/40" />
        <StreetStickers />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-20">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-xs uppercase tracking-[0.4em] text-gold font-bold mb-4">
            Dressedin by Urbanz
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
            WEAR YOUR<br /><span className="gold-text">IDENTITY.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-5 max-w-md text-sm sm:text-base text-foreground/80">
            Streetwear energy. Luxury polish. Pieces built for people who refuse to blend in.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
            className="mt-8 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="hero-shop-btn"
              className="btn-gold px-8 py-4 font-display font-bold text-sm uppercase tracking-[0.2em] inline-flex items-center gap-2">
              Shop the drop <ArrowRight size={16} />
            </Link>
            <Link to="/shop?category=streetwear" data-testid="hero-streetwear-btn"
              className="px-8 py-4 border border-gold/60 text-gold font-display font-bold text-sm uppercase tracking-[0.2em] hover:bg-gold/10 transition-colors">
              Streetwear
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#262626] bg-ink-surface overflow-hidden py-3">
        <div className="animate-marquee whitespace-nowrap flex gap-12 w-max">
          {Array(2).fill(["WEAR YOUR IDENTITY", "COLLECT 5 STAMPS = 40% OFF", "NEW DROPS EVERY FRIDAY", "FREE SHIPPING OVER ₹1,499", "LIMITED EDITIONS ONLY"]).flat().map((t, i) => (
            <span key={`${t}-${i}`} className="font-display font-bold text-sm tracking-[0.3em] text-gold/80">{t} ✦</span>
          ))}
        </div>
      </div>
      <StickerMarquee />

      {/* STAMP CARD OFFER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="border border-gold/50 bg-ink-surface p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between"
          data-testid="stamp-offer-banner">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">Loyalty, the DN way</p>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">
              Collect 5 stamps. <span className="gold-text">Unlock 40% off.</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-2">Earn 1 stamp card with every order. Redeem 5 for 40% off your next one.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            {Array(5).fill(0).map((_, i) => (
              <div key={`stamp-${i}`} className="h-14 w-14 border border-gold/60 flex items-center justify-center bg-ink">
                <img src="/logo.png" alt="DN stamp" className="h-9 w-9 object-contain" />
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CATEGORY BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">Collections</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-12">Pick your lane</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {CATS.map((c, i) => (
            <motion.div key={c.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }}
              className={i === 0 ? "md:row-span-2" : ""}>
              <Link to={`/shop?category=${c.key}`} data-testid={`category-${c.key}`} className="group relative block overflow-hidden border border-[#262626] hover:border-gold/60 transition-colors">
                <div className={`${i === 0 ? "aspect-[3/4] md:aspect-auto md:h-full md:min-h-[560px]" : "aspect-[4/3]"} w-full`}>
                  <img src={c.img} alt={c.label} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-display font-extrabold text-2xl tracking-tight">{c.label}</h3>
                  <p className="text-xs text-foreground/70 mt-1">{c.desc}</p>
                  <span className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] font-bold mt-3 group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.36 }}
            className="border border-gold/40 bg-ink-surface p-8 flex flex-col justify-between">
            <p className="font-display text-2xl font-bold leading-snug">"Style is the only introduction you need."</p>
            <Link to="/shop" className="text-gold text-xs uppercase tracking-[0.2em] font-bold inline-flex items-center gap-2 mt-6">
              Shop all <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">Handpicked</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Featured heat</h2>
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors hidden sm:block">View all</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* BRAND STORY */}
      <section ref={storyRef} className="relative border-y border-[#262626] bg-ink-surface overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <motion.div style={{ y }} className="relative">
            <img src="https://images.unsplash.com/photo-1768845431893-8e28ed7bf79e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Gold and black editorial" loading="lazy" className="w-full aspect-[4/5] object-cover border border-[#262626]" />
            <img src="/logo.png" alt="" className="absolute -bottom-6 -right-6 h-28 w-28 object-contain bg-ink border border-gold/40 p-2" />
          </motion.div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-4">The brand</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
              Born in the streets.<br />Finished like <span className="gold-text">couture.</span>
            </h2>
            <p className="mt-6 text-foreground/75 leading-relaxed">
              Dressedin by Urbanz started with one belief — your clothes should say who you are before you do.
              We merge the rawness of street culture with the precision of luxury tailoring. Every stitch,
              every drop, every gold thread is deliberate.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[["400+", "GSM heavyweight fleece"], ["48h", "Dispatch on every order"], ["100%", "Limited-run drops"]].map(([n, t]) => (
                <div key={t}>
                  <p className="font-display font-black text-3xl gold-text">{n}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="new-arrivals-section">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">Just landed</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">New arrivals</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
          {newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* CUSTOMIZE STUDIO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid md:grid-cols-2 border border-[#262626] overflow-hidden">
          <div className="p-8 md:p-12 flex flex-col justify-center bg-ink-surface">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">DN Studio</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
              Customize your <span className="gold-text">T-shirts, shirts &amp; joggers.</span>
            </h2>
            <p className="text-sm text-foreground/70 mt-4 leading-relaxed">
              Your text. Your color. Your identity — printed to order. Pick a piece, drop your slogan,
              and our Bhopal studio hand-finishes it for you.
            </p>
            <ul className="mt-6 space-y-2 text-xs text-muted-foreground">
              <li>— Custom prints from just ₹149 extra</li>
              <li>— Gold, white or black print on any base color</li>
              <li>— Sizes S to XXL, single pieces or crew packs up to 10</li>
            </ul>
            <Link to="/customize" data-testid="customize-cta"
              className="btn-gold mt-8 px-8 py-4 font-display font-bold text-sm uppercase tracking-[0.2em] inline-flex items-center gap-2 w-fit">
              Start customizing <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative min-h-[320px]">
            <img src="https://images.pexels.com/photos/8532616/pexels-photo-8532616.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Customize your apparel" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/30" />
            <p className="absolute inset-0 flex items-center justify-center font-display font-black text-3xl uppercase tracking-[0.3em] gold-text"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.8)" }}>
              Your text here
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER NOTE */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-24 text-center">
        <img src="/logo.png" alt="DN monogram" className="h-16 w-16 object-contain mx-auto mb-6" />
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-4">A note from the founder</p>
        <blockquote className="font-display text-2xl sm:text-3xl font-bold leading-snug tracking-tight" data-testid="founder-note">
          "I started Dressedin because I was tired of choosing between cheap hype and silent luxury.
          Every piece we drop is designed in Bhopal, made to be lived in — and made to say something
          before you do. Thanks for wearing your identity with us."
        </blockquote>
        <p className="mt-6 text-sm text-muted-foreground">— Founder, Dressedin by Urbanz</p>
      </section>

      {/* LOOKBOOK */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24" data-testid="lookbook">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">The lookbook</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">Recent drops, on the streets</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {LOOKBOOK.map((l, i) => (
            <motion.div key={l.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }}
              className="group relative overflow-hidden border border-[#262626] aspect-[3/4] hover:border-gold/50 transition-colors"
              data-testid={`lookbook-${i}`}>
              <img src={l.img} alt={l.title} loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <p className="absolute bottom-0 p-4 text-xs font-bold tracking-widest uppercase text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300">{l.title}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS + TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[[Truck, "Free shipping", "On all orders above ₹1,499 across India"],
            [ShieldCheck, "Secure payments", "UPI, cards, net banking & wallets via Razorpay"],
            [RefreshCcw, "Easy returns", "7-day hassle-free returns & refunds"]].map(([Icon, t, d]) => (
            <div key={t} className="border border-[#262626] bg-ink-surface p-6 flex gap-4 items-start hover:border-gold/50 transition-colors">
              <Icon className="text-gold shrink-0" size={22} />
              <div>
                <p className="font-display font-bold">{t}</p>
                <p className="text-xs text-muted-foreground mt-1">{d}</p>
              </div>
            </div>
          ))}
        </div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">Worn &amp; vouched</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="border border-[#262626] bg-ink-surface p-8" data-testid={`testimonial-${i}`}>
              <Quote size={20} className="text-gold mb-4" />
              <p className="text-sm leading-relaxed text-foreground/80">{t.text}</p>
              <p className="mt-6 font-display font-bold text-sm">{t.name} <span className="text-muted-foreground font-normal">— {t.city}</span></p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
